import { skipToken, useQuery } from '@tanstack/react-query'

import env from '~/env'

import { useSentryLogging } from '../shared/useSentryLogging'
import {
    betterstackStatusPageResponseSchema,
    IncidentStatusReport,
    isIncidentStatusReport,
    isMaintenanceStatusReport,
    isStatusUpdate,
    isUnresolvedStatusReport,
    MaintenanceStatusReport,
    type StatusUpdate,
} from './schema'
import { getReportData, shouldReplaceLatestReport, StatusPageData } from './utils'

const REFETCH_INTERVAL = 60 * 1000 // 1 minute
const BASE_ERROR_MESSAGE = `Fetching Better Stack status page failed for tenant ${env.VITE_TENANT_NAME}`

const fetchStatusPage = async () => {
    // Ensure single trailing slash before 'index.json'
    const url = `${(env.VITE_BETTERSTACK_STATUS_PAGE_URL ?? '').replace(/\/+$/, '')}/index.json`
    let response

    try {
        response = await fetch(url)
    } catch (error) {
        if (error instanceof Error) {
            throw new Error(`${BASE_ERROR_MESSAGE}. Response: ${error.message}`)
        } else {
            throw new Error(`Unknown error. ${BASE_ERROR_MESSAGE}`)
        }
    }

    if (!response.ok) {
        throw new Error(`${BASE_ERROR_MESSAGE}. Response: ${response.status}`)
    }

    return betterstackStatusPageResponseSchema.parse(await response.json())
}

export const useBetterStackStatusPageQuery = () => {
    const query = useQuery({
        queryKey: ['betterstackStatusPage'],
        queryFn: env.VITE_BETTERSTACK_STATUS_PAGE_URL ? () => fetchStatusPage() : skipToken,
        select: (data): StatusPageData => {
            let latestIncidentReport: IncidentStatusReport | null = null
            let latestMaintenanceReport: MaintenanceStatusReport | null = null
            const statusUpdateById = new Map<string, StatusUpdate>()

            for (const item of data.included) {
                if (isUnresolvedStatusReport(item)) {
                    if (isMaintenanceStatusReport(item)) {
                        if (shouldReplaceLatestReport(item, latestMaintenanceReport)) {
                            latestMaintenanceReport = item
                        }
                    } else if (isIncidentStatusReport(item)) {
                        if (shouldReplaceLatestReport(item, latestIncidentReport)) {
                            latestIncidentReport = item
                        }
                    }
                } else if (isStatusUpdate(item)) {
                    statusUpdateById.set(item.id, item)
                }
            }

            return {
                incident: getReportData(latestIncidentReport, statusUpdateById),
                maintenance: getReportData(latestMaintenanceReport, statusUpdateById),
            }
        },
        refetchInterval: REFETCH_INTERVAL,
        staleTime: REFETCH_INTERVAL, // Consider data fresh for same duration as refetch interval to avoid extra refetches
    })

    useSentryLogging({ key: 'useBetterStackStatusPageQuery', queryResult: query })

    return query
}
